import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useKeycloak } from '@/composables/useKeycloak'
import { getAppConfig } from '@/config'

export const useAuthStore = defineStore('auth', () => {
  const isInitialized = ref(false)
  const initError = ref<string | null>(null)

  const { init: kcInit, logout: kcLogout, getAccessToken, hasRole, state } = useKeycloak()

  const isAuthenticated = computed(() => state.value !== null)
  const accessToken = computed(() => getAccessToken())
  const role = computed(() => {
    if (!isAuthenticated.value) return null
    return hasRole('super_admin') ? 'super_admin' : null
  })

  function isSuperAdmin(): boolean {
    return hasRole('super_admin')
  }

  // A single shared init promise guarantees Keycloak init runs exactly once even
  // if both main.ts and the router's authGuard call initialize() concurrently.
  let initPromise: Promise<void> | null = null

  async function runInit(): Promise<void> {
    // Only mark initialized on SUCCESS. A failure (e.g. config not yet loaded,
    // or a transient Keycloak error) must not latch isInitialized=true and block
    // a later successful attempt — that was the empty-page bug.
    const config = getAppConfig()
    await kcInit({
      url: config.keycloakUrl,
      realm: config.keycloakRealm,
      clientId: config.keycloakClient,
    })
    if (!isSuperAdmin()) {
      throw new Error('Insufficient permissions: super_admin role required')
    }
    isInitialized.value = true
  }

  async function initialize(): Promise<void> {
    if (isInitialized.value) return
    if (initPromise) return initPromise

    initPromise = runInit().catch((e) => {
      initError.value = e instanceof Error ? e.message : 'Auth initialization failed'
      initPromise = null // allow a retry after a failed attempt
      throw e
    })
    return initPromise
  }

  function logout(): void {
    kcLogout()
    isInitialized.value = false
  }

  return {
    isInitialized,
    isAuthenticated,
    accessToken,
    role,
    initError,
    initialize,
    logout,
    isSuperAdmin,
  }
})
